import React, { useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Tooltip,
  Typography,
  Descriptions,
  Divider,
  Tabs,
  InputNumber,
  Checkbox,
  Collapse,
  Row,
  Col,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  FileExcelOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';
import dayjs from 'dayjs';

const { Option } = Select;
const { Text, Title } = Typography;

const QUERY_KEY = 'smart-parser-configs';

const FORMAT_TYPE_COLORS = {
  column_based: 'blue',
  text_marker: 'green',
  symbol_based: 'orange',
  mixed: 'purple',
};

const STATUS_COLORS = {
  active: 'green',
  testing: 'orange',
  draft: 'default',
  archived: 'red',
};

const COLUMN_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

// Hours columns - for timesheet configs
const HOURS_COLUMN_FIELDS = [
  { key: 'regularHours', label: 'Regular Hours' },
  { key: 'overtime', label: 'Overtime' },
  { key: 'weekendOvertime', label: 'Weekend Overtime' },
  { key: 'nightShift', label: 'Night Shift' },
  { key: 'publicHolidayHours', label: 'Public Holiday' },
  { key: 'annualLeave', label: 'Annual Leave' },
  { key: 'sickLeave', label: 'Sick Leave' },
  { key: 'paidLeave', label: 'Paid Leave' },
  { key: 'unpaidLeave', label: 'Unpaid Leave' },
  { key: 'totalHours', label: 'Total Hours' },
];

// Allowance columns - for salary configs
const ALLOWANCE_COLUMN_FIELDS = [
  { key: 'bonus', label: 'Bonus' },
  { key: 'mealAllowance', label: 'Meal Allowance' },
  { key: 'holidayAllowance', label: 'Holiday Allowance' },
  { key: 'travelAllowance', label: 'Travel Allowance' },
];

// Combined for backwards compatibility
const COLUMN_MAPPING_FIELDS = [...HOURS_COLUMN_FIELDS, ...ALLOWANCE_COLUMN_FIELDS];

const CONFIG_TYPE_COLORS = {
  timesheet: 'blue',
  salary: 'green',
};

export default function ParserConfigsPanel({ configs = [], isLoading, onRefresh }) {
  const { t } = useTranslation(['tables', 'forms', 'messages']);
  const queryClient = useQueryClient();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [formatModalVisible, setFormatModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [editingFormatIndex, setEditingFormatIndex] = useState(null);
  const [importSourceConfig, setImportSourceConfig] = useState(null);
  const [selectedImportFormats, setSelectedImportFormats] = useState([]);
  const [form] = Form.useForm();
  const [cloneForm] = Form.useForm();
  const [formatForm] = Form.useForm();

  // Local state for complex nested objects
  const [configType, setConfigType] = useState('timesheet');
  const [multipleFormats, setMultipleFormats] = useState(false);
  const [formatMappings, setFormatMappings] = useState([]);
  const [skipSheets, setSkipSheets] = useState('');
  const [columnMapping, setColumnMapping] = useState({});
  const [textMarkerConfig, setTextMarkerConfig] = useState({
    enabled: false,
    sourceColumn: 'regularHours',
    hoursPerDay: 8,
    markers: {
      annualLeave: '',
      sickLeave: '',
      paidLeave: '',
      unpaidLeave: '',
      publicHoliday: '',
    },
  });

  const sp = (key) => t(`smartParser.${key}`, { ns: 'tables', defaultValue: key });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => API.smartParser.updateConfig(id, data),
    onSuccess: () => {
      message.success(t('updateSuccess', { ns: 'messages' }));
      queryClient.invalidateQueries([QUERY_KEY]);
      handleCloseEditModal();
      onRefresh?.();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || t('updateError', { ns: 'messages' }));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => API.smartParser.deleteConfig(id),
    onSuccess: () => {
      message.success(t('deleteSuccess', { ns: 'messages' }));
      queryClient.invalidateQueries([QUERY_KEY]);
      onRefresh?.();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || t('deleteError', { ns: 'messages' }));
    },
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: (id) => API.smartParser.activateConfig(id),
    onSuccess: () => {
      message.success('Configuration activated successfully');
      queryClient.invalidateQueries([QUERY_KEY]);
      onRefresh?.();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to activate configuration');
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: ({ id, name }) => API.smartParser.cloneConfig(id, name),
    onSuccess: () => {
      message.success('Configuration cloned successfully');
      queryClient.invalidateQueries([QUERY_KEY]);
      handleCloseCloneModal();
      onRefresh?.();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to clone configuration');
    },
  });

  const handleOpenEditModal = (config) => {
    setSelectedConfig(config);

    // Set form values
    form.setFieldsValue({
      name: config.name,
      description: config.description,
      status: config.status,
      // Excel config
      headerRow: config.excelConfig?.headerDetection?.fixedRow || 5,
      dataStartOffset: config.excelConfig?.dataRows?.startOffset || 1,
      dataRowCount: config.excelConfig?.dataRows?.fixedCount || 31,
      employeeNameSource: config.excelConfig?.employeeNameSource?.type || 'sheet_name',
      jmbgEnabled: config.excelConfig?.jmbgExtraction?.enabled ?? true,
      jmbgRow: config.excelConfig?.jmbgExtraction?.location?.fixedCell?.row || 6,
      jmbgColumn: config.excelConfig?.jmbgExtraction?.location?.fixedCell?.column || 'C',
    });

    // Set local state
    setConfigType(config.configType || 'timesheet');
    setSkipSheets(config.excelConfig?.skipSheets?.join(', ') || '');
    setMultipleFormats(config.multipleFormats || false);
    setFormatMappings(config.formatMappings || []);

    // Set column mapping
    const cm = {};
    COLUMN_MAPPING_FIELDS.forEach(({ key }) => {
      cm[key] = config.columnMapping?.[key]?.columnLetter || '';
    });
    setColumnMapping(cm);

    // Set text marker config
    const tmc = config.textMarkerConfig || {};
    setTextMarkerConfig({
      enabled: tmc.enabled || false,
      sourceColumn: tmc.sourceColumn || 'regularHours',
      hoursPerDay: tmc.hoursPerDay || 8,
      markers: {
        annualLeave: tmc.markers?.annualLeave?.join(', ') || '',
        sickLeave: tmc.markers?.sickLeave?.join(', ') || '',
        paidLeave: tmc.markers?.paidLeave?.join(', ') || '',
        unpaidLeave: tmc.markers?.unpaidLeave?.join(', ') || '',
        publicHoliday: tmc.markers?.publicHoliday?.join(', ') || '',
      },
    });

    setEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setSelectedConfig(null);
    form.resetFields();
    setConfigType('timesheet');
    setSkipSheets('');
    setMultipleFormats(false);
    setFormatMappings([]);
    setColumnMapping({});
    setTextMarkerConfig({
      enabled: false,
      sourceColumn: 'regularHours',
      hoursPerDay: 8,
      markers: {
        annualLeave: '',
        sickLeave: '',
        paidLeave: '',
        unpaidLeave: '',
        publicHoliday: '',
      },
    });
  };

  const handleOpenViewModal = (config) => {
    setSelectedConfig(config);
    setViewModalVisible(true);
  };

  const handleCloseViewModal = () => {
    setViewModalVisible(false);
    setSelectedConfig(null);
  };

  const handleOpenCloneModal = (config) => {
    setSelectedConfig(config);
    cloneForm.setFieldsValue({
      name: `${config.name} (copy)`,
    });
    setCloneModalVisible(true);
  };

  const handleCloseCloneModal = () => {
    setCloneModalVisible(false);
    setSelectedConfig(null);
    cloneForm.resetFields();
  };

  const handleSubmitEdit = async (values) => {
    // Build the full config object
    const skipSheetsArray = skipSheets
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    // Build column mapping
    const builtColumnMapping = {};
    COLUMN_MAPPING_FIELDS.forEach(({ key }) => {
      if (columnMapping[key]) {
        builtColumnMapping[key] = {
          columnLetter: columnMapping[key].toUpperCase(),
          dataType: 'number',
          defaultValue: 0,
        };
      }
    });

    // Build text marker config
    const parseMarkers = (str) =>
      str ? str.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean) : [];

    const builtTextMarkerConfig = {
      enabled: textMarkerConfig.enabled,
      sourceColumn: textMarkerConfig.sourceColumn,
      hoursPerDay: textMarkerConfig.hoursPerDay,
      markers: {
        annualLeave: parseMarkers(textMarkerConfig.markers.annualLeave),
        sickLeave: parseMarkers(textMarkerConfig.markers.sickLeave),
        paidLeave: parseMarkers(textMarkerConfig.markers.paidLeave),
        unpaidLeave: parseMarkers(textMarkerConfig.markers.unpaidLeave),
        publicHoliday: parseMarkers(textMarkerConfig.markers.publicHoliday),
      },
    };

    const updateData = {
      name: values.name,
      description: values.description,
      status: values.status,
      configType,
      multipleFormats,
      formatMappings: multipleFormats ? formatMappings : [],
      excelConfig: {
        skipSheets: skipSheetsArray,
        headerDetection: {
          type: 'fixed_row',
          fixedRow: values.headerRow,
        },
        dataRows: {
          startOffset: values.dataStartOffset,
          endDetection: 'fixed_count',
          fixedCount: values.dataRowCount,
        },
        employeeNameSource: {
          type: values.employeeNameSource,
        },
        jmbgExtraction: {
          enabled: values.jmbgEnabled,
          location: {
            type: 'fixed_cell',
            fixedCell: {
              row: values.jmbgRow,
              column: values.jmbgColumn,
            },
          },
        },
      },
      columnMapping: builtColumnMapping,
      textMarkerConfig: builtTextMarkerConfig,
    };

    updateMutation.mutate({ id: selectedConfig._id, data: updateData });
  };

  const handleSubmitClone = async (values) => {
    cloneMutation.mutate({ id: selectedConfig._id, name: values.name });
  };

  const handleDelete = (configId) => {
    deleteMutation.mutate(configId);
  };

  const handleActivate = (configId) => {
    activateMutation.mutate(configId);
  };

  // Format Mapping handlers
  const handleAddFormatMapping = () => {
    setEditingFormatIndex(null);
    formatForm.resetFields();
    formatForm.setFieldsValue({
      name: '',
      patterns: '',
      isDefault: formatMappings.length === 0,
    });
    setFormatModalVisible(true);
  };

  const handleEditFormatMapping = (index) => {
    const format = formatMappings[index];
    setEditingFormatIndex(index);
    formatForm.setFieldsValue({
      name: format.name,
      patterns: format.detection?.patterns?.join(', ') || '',
      isDefault: format.isDefault || false,
      // Structure settings
      headerRow: format.excelConfig?.headerDetection?.fixedRow || 5,
      dataStartOffset: format.excelConfig?.dataRows?.startOffset || 1,
      dataRowCount: format.excelConfig?.dataRows?.fixedCount || 31,
      skipSheets: format.excelConfig?.skipSheets?.join(', ') || '',
      employeeNameSource: format.excelConfig?.employeeNameSource?.type || 'sheet_name',
      jmbgEnabled: format.excelConfig?.jmbgExtraction?.enabled ?? true,
      // Column mappings
      ...Object.fromEntries(
        COLUMN_MAPPING_FIELDS.map(({ key }) => [
          `col_${key}`,
          format.columnMapping?.[key]?.columnLetter || '',
        ])
      ),
      // Text marker config
      textMarkersEnabled: format.textMarkerConfig?.enabled || false,
      textMarkersSourceColumn: format.textMarkerConfig?.sourceColumn || 'regularHours',
      textMarkersHoursPerDay: format.textMarkerConfig?.hoursPerDay || 8,
      textMarkersAnnualLeave: format.textMarkerConfig?.markers?.annualLeave?.join(', ') || '',
      textMarkersSickLeave: format.textMarkerConfig?.markers?.sickLeave?.join(', ') || '',
      textMarkersPaidLeave: format.textMarkerConfig?.markers?.paidLeave?.join(', ') || '',
      textMarkersUnpaidLeave: format.textMarkerConfig?.markers?.unpaidLeave?.join(', ') || '',
      textMarkersPublicHoliday: format.textMarkerConfig?.markers?.publicHoliday?.join(', ') || '',
      // Summary sheet config
      summarySheetEnabled: format.summarySheetConfig?.enabled || false,
      summarySheetPattern: format.summarySheetConfig?.sheetNamePattern || '',
      summarySheetHeaderRow: format.summarySheetConfig?.headerRow || 1,
      summarySheetDataStartRow: format.summarySheetConfig?.dataStartRow || 2,
      summarySheetSkipIndividual: format.summarySheetConfig?.skipIndividualSheets ?? true,
      summarySheetSkipRowKeywords: format.summarySheetConfig?.skipRowKeywords?.join(', ') || '',
      summaryCol_employeeName: format.summarySheetConfig?.columnMapping?.employeeName || '',
      summaryCol_jmbg: format.summarySheetConfig?.columnMapping?.jmbg || '',
      summaryCol_regularHours: format.summarySheetConfig?.columnMapping?.regularHours || '',
      summaryCol_overtime: format.summarySheetConfig?.columnMapping?.overtime || '',
      summaryCol_weekendOvertime: format.summarySheetConfig?.columnMapping?.weekendOvertime || '',
      summaryCol_publicHolidayHours: format.summarySheetConfig?.columnMapping?.publicHolidayHours || '',
      summaryCol_annualLeave: format.summarySheetConfig?.columnMapping?.annualLeave || '',
      summaryCol_sickLeave: format.summarySheetConfig?.columnMapping?.sickLeave || '',
      summaryCol_paidLeave: format.summarySheetConfig?.columnMapping?.paidLeave || '',
      summaryCol_unpaidLeave: format.summarySheetConfig?.columnMapping?.unpaidLeave || '',
      summaryCol_totalHours: format.summarySheetConfig?.columnMapping?.totalHours || '',
      // Allowance columns
      summaryCol_bonus: format.summarySheetConfig?.columnMapping?.bonus || '',
      summaryCol_mealAllowance: format.summarySheetConfig?.columnMapping?.mealAllowance || '',
      summaryCol_holidayAllowance: format.summarySheetConfig?.columnMapping?.holidayAllowance || '',
      summaryCol_travelAllowance: format.summarySheetConfig?.columnMapping?.travelAllowance || '',
    });
    setFormatModalVisible(true);
  };

  const handleDeleteFormatMapping = (index) => {
    const newMappings = [...formatMappings];
    newMappings.splice(index, 1);
    setFormatMappings(newMappings);
  };

  const handleSaveFormatMapping = (values) => {
    const parseMarkers = (str) =>
      str ? str.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean) : [];

    const formatConfig = {
      id: editingFormatIndex !== null
        ? formatMappings[editingFormatIndex].id
        : `format_${Date.now()}`,
      name: values.name,
      detection: {
        type: 'filename_pattern',
        patterns: values.patterns
          ? values.patterns.split(',').map((p) => p.trim().toLowerCase()).filter(Boolean)
          : [],
        caseSensitive: false,
      },
      excelConfig: {
        skipSheets: values.skipSheets
          ? values.skipSheets.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
          : [],
        headerDetection: {
          type: 'fixed_row',
          fixedRow: values.headerRow || 5,
        },
        dataRows: {
          startOffset: values.dataStartOffset || 1,
          endDetection: 'fixed_count',
          fixedCount: values.dataRowCount || 31,
        },
        employeeNameSource: {
          type: values.employeeNameSource || 'sheet_name',
        },
        jmbgExtraction: {
          enabled: values.jmbgEnabled ?? true,
          location: {
            type: 'fixed_cell',
            fixedCell: {
              row: 6,
              column: 'C',
            },
          },
        },
      },
      columnMapping: Object.fromEntries(
        COLUMN_MAPPING_FIELDS.map(({ key }) => [
          key,
          values[`col_${key}`]
            ? { columnLetter: values[`col_${key}`].toUpperCase(), dataType: 'number', defaultValue: 0 }
            : null,
        ]).filter(([_, v]) => v)
      ),
      textMarkerConfig: {
        enabled: values.textMarkersEnabled || false,
        sourceColumn: values.textMarkersSourceColumn || 'regularHours',
        hoursPerDay: values.textMarkersHoursPerDay || 8,
        markers: {
          annualLeave: parseMarkers(values.textMarkersAnnualLeave),
          sickLeave: parseMarkers(values.textMarkersSickLeave),
          paidLeave: parseMarkers(values.textMarkersPaidLeave),
          unpaidLeave: parseMarkers(values.textMarkersUnpaidLeave),
          publicHoliday: parseMarkers(values.textMarkersPublicHoliday),
        },
      },
      summarySheetConfig: {
        enabled: values.summarySheetEnabled || false,
        sheetNamePattern: values.summarySheetPattern?.toLowerCase() || '',
        headerRow: values.summarySheetHeaderRow || 1,
        dataStartRow: values.summarySheetDataStartRow || 2,
        skipIndividualSheets: values.summarySheetSkipIndividual ?? true,
        skipRowKeywords: values.summarySheetSkipRowKeywords
          ? values.summarySheetSkipRowKeywords.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
          : [],
        columnMapping: {
          employeeName: values.summaryCol_employeeName?.toUpperCase() || '',
          jmbg: values.summaryCol_jmbg?.toUpperCase() || '',
          regularHours: values.summaryCol_regularHours?.toUpperCase() || '',
          overtime: values.summaryCol_overtime?.toUpperCase() || '',
          weekendOvertime: values.summaryCol_weekendOvertime?.toUpperCase() || '',
          publicHolidayHours: values.summaryCol_publicHolidayHours?.toUpperCase() || '',
          annualLeave: values.summaryCol_annualLeave?.toUpperCase() || '',
          sickLeave: values.summaryCol_sickLeave?.toUpperCase() || '',
          paidLeave: values.summaryCol_paidLeave?.toUpperCase() || '',
          unpaidLeave: values.summaryCol_unpaidLeave?.toUpperCase() || '',
          totalHours: values.summaryCol_totalHours?.toUpperCase() || '',
          // Allowance columns
          bonus: values.summaryCol_bonus?.toUpperCase() || '',
          mealAllowance: values.summaryCol_mealAllowance?.toUpperCase() || '',
          holidayAllowance: values.summaryCol_holidayAllowance?.toUpperCase() || '',
          travelAllowance: values.summaryCol_travelAllowance?.toUpperCase() || '',
        },
      },
      isDefault: values.isDefault || false,
    };

    const newMappings = [...formatMappings];
    if (editingFormatIndex !== null) {
      newMappings[editingFormatIndex] = formatConfig;
    } else {
      newMappings.push(formatConfig);
    }

    // If this is set as default, unset others
    if (formatConfig.isDefault) {
      newMappings.forEach((m, i) => {
        if (i !== (editingFormatIndex ?? newMappings.length - 1)) {
          m.isDefault = false;
        }
      });
    }

    setFormatMappings(newMappings);
    setFormatModalVisible(false);
    formatForm.resetFields();
    setEditingFormatIndex(null);
  };

  const columns = [
    {
      title: sp('configName'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.description.length > 50
                  ? `${record.description.substring(0, 50)}...`
                  : record.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'configType',
      key: 'configType',
      render: (type) => (
        <Tag color={CONFIG_TYPE_COLORS[type] || 'blue'}>
          {(type || 'timesheet').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: sp('formatType'),
      dataIndex: 'formatType',
      key: 'formatType',
      render: (type) => (
        <Tag color={FORMAT_TYPE_COLORS[type] || 'default'}>
          {type?.replace('_', ' ').toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: sp('company'),
      dataIndex: 'companyId',
      key: 'companyId',
      render: (company) => company?.name || <Text type="secondary">Global</Text>,
    },
    {
      title: t('status', { ns: 'tables' }),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>
          {status?.toUpperCase() || 'DRAFT'}
        </Tag>
      ),
    },
    {
      title: sp('version'),
      dataIndex: 'version',
      key: 'version',
      render: (version) => `v${version}`,
    },
    {
      title: sp('testAccuracy'),
      dataIndex: ['testResults', 'accuracy'],
      key: 'accuracy',
      render: (accuracy) =>
        accuracy ? (
          <Text type={accuracy >= 90 ? 'success' : accuracy >= 70 ? 'warning' : 'danger'}>
            {accuracy.toFixed(1)}%
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: sp('lastUpdated'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => dayjs(date).format('DD.MM.YYYY HH:mm'),
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={sp('viewDetails')}>
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleOpenViewModal(record)} />
          </Tooltip>
          <Tooltip title={sp('edit')}>
            <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenEditModal(record)} />
          </Tooltip>
          <Tooltip title={sp('clone')}>
            <Button type="text" icon={<CopyOutlined />} onClick={() => handleOpenCloneModal(record)} />
          </Tooltip>
          {record.status !== 'active' && (
            <Popconfirm
              title={sp('activateConfig')}
              description={sp('activateConfigDesc')}
              onConfirm={() => handleActivate(record._id)}
              okText={sp('yes')}
              cancelText={sp('no')}
            >
              <Tooltip title={sp('activate')}>
                <Button type="text" icon={<CheckCircleOutlined />} style={{ color: 'green' }} />
              </Tooltip>
            </Popconfirm>
          )}
          {record.status !== 'active' && (
            <Popconfirm
              title={sp('archiveConfig')}
              description={sp('archiveConfigDesc')}
              onConfirm={() => handleDelete(record._id)}
              okText={sp('yes')}
              cancelText={sp('no')}
            >
              <Tooltip title={sp('archive')}>
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const renderColumnMappings = (columnMappingData) => {
    if (!columnMappingData) return <Text type="secondary">No mappings defined</Text>;

    const mappings = Object.entries(columnMappingData)
      .filter(([_, value]) => value)
      .map(([key, value]) => (
        <Tag key={key} style={{ marginBottom: 4 }}>
          {key}: {typeof value === 'object' ? value.columnLetter : value}
        </Tag>
      ));

    return mappings.length > 0 ? mappings : <Text type="secondary">No mappings defined</Text>;
  };

  // Edit modal tabs
  const editModalTabs = [
    {
      key: 'basic',
      label: 'Basic Info',
      children: (
        <>
          <Form.Item
            name="name"
            label={sp('configName')}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label={sp('description')}>
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label={t('status', { ns: 'tables' })}>
                <Select>
                  <Option value="draft">{sp('draft')}</Option>
                  <Option value="testing">{sp('testing')}</Option>
                  <Option value="active">{sp('active')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Config Type">
                <Select value={configType} onChange={setConfigType}>
                  <Option value="timesheet">
                    <Tag color="blue">Timesheet</Tag> - Parse hours (regular, overtime, leaves)
                  </Option>
                  <Option value="salary">
                    <Tag color="green">Salary</Tag> - Parse allowances (bonus, meal, travel)
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Checkbox
              checked={multipleFormats}
              onChange={(e) => setMultipleFormats(e.target.checked)}
            >
              <Space>
                <span>Multiple Excel formats</span>
                <Tooltip title="Enable if EML contains Excel files with different structures">
                  <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            </Checkbox>
          </Form.Item>
        </>
      ),
    },
    {
      key: 'structure',
      label: 'Structure Settings',
      children: (
        <>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="headerRow" label="Header Row">
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dataStartOffset" label="Data Start Offset">
                <InputNumber min={0} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dataRowCount" label="Data Row Count">
                <InputNumber min={1} max={366} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Skip Sheets (comma separated)">
            <Input
              value={skipSheets}
              onChange={(e) => setSkipSheets(e.target.value)}
              placeholder="source, employees, outputs, bank, cash"
            />
          </Form.Item>

          <Form.Item name="employeeNameSource" label="Employee Name Source">
            <Select>
              <Option value="sheet_name">Sheet Name</Option>
              <Option value="fixed_cell">Fixed Cell</Option>
              <Option value="filename_parse">Filename Parse</Option>
            </Select>
          </Form.Item>

          <Divider orientation="left" plain>JMBG Extraction</Divider>

          <Form.Item name="jmbgEnabled" valuePropName="checked">
            <Checkbox>Enable JMBG Extraction</Checkbox>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="jmbgRow" label="JMBG Row">
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="jmbgColumn" label="JMBG Column">
                <Select>
                  {COLUMN_OPTIONS.map((col) => (
                    <Option key={col} value={col}>{col}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 'columns',
      label: 'Column Mapping',
      children: (
        <>
          {configType === 'timesheet' && (
            <>
              <Divider orientation="left" plain>Hours Columns</Divider>
              <Row gutter={[16, 8]}>
                {HOURS_COLUMN_FIELDS.map(({ key, label }) => (
                  <Col span={12} key={key}>
                    <Form.Item label={label} style={{ marginBottom: 8 }}>
                      <Select
                        allowClear
                        value={columnMapping[key] || undefined}
                        onChange={(val) => setColumnMapping({ ...columnMapping, [key]: val })}
                        placeholder="Select column"
                      >
                        {COLUMN_OPTIONS.map((col) => (
                          <Option key={col} value={col}>{col}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {configType === 'salary' && (
            <>
              <Divider orientation="left" plain>Allowance Columns</Divider>
              <Row gutter={[16, 8]}>
                {ALLOWANCE_COLUMN_FIELDS.map(({ key, label }) => (
                  <Col span={12} key={key}>
                    <Form.Item label={label} style={{ marginBottom: 8 }}>
                      <Select
                        allowClear
                        value={columnMapping[key] || undefined}
                        onChange={(val) => setColumnMapping({ ...columnMapping, [key]: val })}
                        placeholder="Select column"
                      >
                        {COLUMN_OPTIONS.map((col) => (
                          <Option key={col} value={col}>{col}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </>
      ),
    },
    {
      key: 'textMarkers',
      label: 'Text Markers',
      children: (
        <>
          <Form.Item>
            <Checkbox
              checked={textMarkerConfig.enabled}
              onChange={(e) => setTextMarkerConfig({ ...textMarkerConfig, enabled: e.target.checked })}
            >
              <Space>
                <span>Enable Text Marker Detection</span>
                <Tooltip title="When cells contain text like 'sick leave' instead of numbers, convert them to hours">
                  <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            </Checkbox>
          </Form.Item>

          {textMarkerConfig.enabled && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Source Column">
                    <Select
                      value={textMarkerConfig.sourceColumn}
                      onChange={(val) => setTextMarkerConfig({ ...textMarkerConfig, sourceColumn: val })}
                    >
                      {COLUMN_MAPPING_FIELDS.map(({ key, label }) => (
                        <Option key={key} value={key}>{label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Hours Per Day">
                    <InputNumber
                      min={1}
                      max={24}
                      value={textMarkerConfig.hoursPerDay}
                      onChange={(val) => setTextMarkerConfig({ ...textMarkerConfig, hoursPerDay: val })}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" plain>Marker Keywords (comma separated)</Divider>

              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Form.Item label="Annual Leave" style={{ marginBottom: 8 }}>
                    <Input
                      value={textMarkerConfig.markers.annualLeave}
                      onChange={(e) => setTextMarkerConfig({
                        ...textMarkerConfig,
                        markers: { ...textMarkerConfig.markers, annualLeave: e.target.value }
                      })}
                      placeholder="annual leave, go, godisnji"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Sick Leave" style={{ marginBottom: 8 }}>
                    <Input
                      value={textMarkerConfig.markers.sickLeave}
                      onChange={(e) => setTextMarkerConfig({
                        ...textMarkerConfig,
                        markers: { ...textMarkerConfig.markers, sickLeave: e.target.value }
                      })}
                      placeholder="sick leave, bolovanje, sick"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Paid Leave" style={{ marginBottom: 8 }}>
                    <Input
                      value={textMarkerConfig.markers.paidLeave}
                      onChange={(e) => setTextMarkerConfig({
                        ...textMarkerConfig,
                        markers: { ...textMarkerConfig.markers, paidLeave: e.target.value }
                      })}
                      placeholder="paid leave, placeno, special leave"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Unpaid Leave" style={{ marginBottom: 8 }}>
                    <Input
                      value={textMarkerConfig.markers.unpaidLeave}
                      onChange={(e) => setTextMarkerConfig({
                        ...textMarkerConfig,
                        markers: { ...textMarkerConfig.markers, unpaidLeave: e.target.value }
                      })}
                      placeholder="unpaid leave, neplaceno"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Public Holiday" style={{ marginBottom: 8 }}>
                    <Input
                      value={textMarkerConfig.markers.publicHoliday}
                      onChange={(e) => setTextMarkerConfig({
                        ...textMarkerConfig,
                        markers: { ...textMarkerConfig.markers, publicHoliday: e.target.value }
                      })}
                      placeholder="public holiday, praznik, holiday"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </>
      ),
    },
    {
      key: 'formats',
      label: 'Format Mappings',
      disabled: !multipleFormats,
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddFormatMapping}
            >
              Add Format Mapping
            </Button>
            <Button
              icon={<ImportOutlined />}
              onClick={() => setImportModalVisible(true)}
            >
              Import from other config
            </Button>
          </div>

          {formatMappings.length === 0 ? (
            <Text type="secondary">No format mappings defined. Add one to handle different Excel structures.</Text>
          ) : (
            <Collapse accordion>
              {formatMappings.map((format, index) => (
                <Collapse.Panel
                  key={format.id}
                  header={
                    <Space>
                      <Text strong>{format.name}</Text>
                      {format.isDefault && <Tag color="green">Default</Tag>}
                      {format.detection?.patterns?.length > 0 && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Patterns: {format.detection.patterns.join(', ')}
                        </Text>
                      )}
                    </Space>
                  }
                  extra={
                    <Space onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditFormatMapping(index);
                        }}
                      />
                      {!format.isDefault && (
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFormatMapping(index);
                          }}
                        />
                      )}
                    </Space>
                  }
                >
                  <Descriptions size="small" column={2} bordered>
                    <Descriptions.Item label="Header Row">
                      {format.excelConfig?.headerDetection?.fixedRow || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Rows">
                      {format.excelConfig?.dataRows?.fixedCount || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Skip Sheets" span={2}>
                      {format.excelConfig?.skipSheets?.join(', ') || 'None'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Employee Name">
                      {format.excelConfig?.employeeNameSource?.type || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="JMBG">
                      {format.excelConfig?.jmbgExtraction?.enabled ? 'Enabled' : 'Disabled'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Text Markers" span={2}>
                      {format.textMarkerConfig?.enabled ? (
                        <span>
                          Enabled (Source: {format.textMarkerConfig.sourceColumn}, {format.textMarkerConfig.hoursPerDay}h/day)
                        </span>
                      ) : 'Disabled'}
                    </Descriptions.Item>
                  </Descriptions>
                  <Divider orientation="left" plain style={{ margin: '12px 0' }}>Column Mapping</Divider>
                  <div>{renderColumnMappings(format.columnMapping)}</div>
                  {format.textMarkerConfig?.enabled && (
                    <>
                      <Divider orientation="left" plain style={{ margin: '12px 0' }}>Text Markers</Divider>
                      <div>
                        {format.textMarkerConfig.markers?.annualLeave?.length > 0 && (
                          <Tag color="blue">Annual: {format.textMarkerConfig.markers.annualLeave.join(', ')}</Tag>
                        )}
                        {format.textMarkerConfig.markers?.sickLeave?.length > 0 && (
                          <Tag color="red">Sick: {format.textMarkerConfig.markers.sickLeave.join(', ')}</Tag>
                        )}
                        {format.textMarkerConfig.markers?.paidLeave?.length > 0 && (
                          <Tag color="green">Paid: {format.textMarkerConfig.markers.paidLeave.join(', ')}</Tag>
                        )}
                        {format.textMarkerConfig.markers?.unpaidLeave?.length > 0 && (
                          <Tag color="orange">Unpaid: {format.textMarkerConfig.markers.unpaidLeave.join(', ')}</Tag>
                        )}
                        {format.textMarkerConfig.markers?.publicHoliday?.length > 0 && (
                          <Tag color="purple">Holiday: {format.textMarkerConfig.markers.publicHoliday.join(', ')}</Tag>
                        )}
                      </div>
                    </>
                  )}
                  {format.summarySheetConfig?.enabled && (
                    <>
                      <Divider orientation="left" plain style={{ margin: '12px 0' }}>Summary Sheet (Row per Employee)</Divider>
                      <div>
                        <Tag color="cyan">Pattern: {format.summarySheetConfig.sheetNamePattern || 'First sheet'}</Tag>
                        <Tag color="cyan">Header Row: {format.summarySheetConfig.headerRow || 1}</Tag>
                        <Tag color="cyan">Data Start: {format.summarySheetConfig.dataStartRow || 2}</Tag>
                        {format.summarySheetConfig.columnMapping?.employeeName && (
                          <Tag>Name: {format.summarySheetConfig.columnMapping.employeeName}</Tag>
                        )}
                        {format.summarySheetConfig.columnMapping?.jmbg && (
                          <Tag>JMBG: {format.summarySheetConfig.columnMapping.jmbg}</Tag>
                        )}
                        {format.summarySheetConfig.columnMapping?.regularHours && (
                          <Tag>Regular: {format.summarySheetConfig.columnMapping.regularHours}</Tag>
                        )}
                      </div>
                    </>
                  )}
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <FileExcelOutlined /> {sp('parserConfigurations')}
          </Title>
          <Text type="secondary">{sp('parserConfigurationsDesc')}</Text>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={configs}
        rowKey="_id"
        loading={isLoading}
        pagination={{ pageSize: 10 }}
        size="middle"
        locale={{
          emptyText: (
            <div style={{ padding: 40 }}>
              <FileExcelOutlined style={{ fontSize: 48, color: '#ccc' }} />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">{sp('noConfigurations')}</Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {sp('useAnalyzerToCreate')}
                </Text>
              </div>
            </div>
          ),
        }}
      />

      {/* Full Edit Modal */}
      <Modal
        title={sp('editConfiguration')}
        open={editModalVisible}
        onCancel={handleCloseEditModal}
        width={800}
        footer={
          <Space>
            <Button onClick={handleCloseEditModal}>{sp('cancel')}</Button>
            <Button type="primary" onClick={() => form.submit()} loading={updateMutation.isPending}>
              {sp('save')}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitEdit}>
          <Tabs items={editModalTabs} />
        </Form>
      </Modal>

      {/* View Details Modal */}
      <Modal
        title={sp('configurationDetails')}
        open={viewModalVisible}
        onCancel={handleCloseViewModal}
        footer={<Button onClick={handleCloseViewModal}>{sp('close')}</Button>}
        width={700}
      >
        {selectedConfig && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={sp('configName')} span={2}>
                {selectedConfig.name}
              </Descriptions.Item>
              <Descriptions.Item label="Config Type">
                <Tag color={CONFIG_TYPE_COLORS[selectedConfig.configType] || 'blue'}>
                  {(selectedConfig.configType || 'timesheet').toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={sp('formatType')}>
                <Tag color={FORMAT_TYPE_COLORS[selectedConfig.formatType]}>
                  {selectedConfig.formatType?.replace('_', ' ').toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('status', { ns: 'tables' })}>
                <Tag color={STATUS_COLORS[selectedConfig.status]}>
                  {selectedConfig.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={sp('version')}>{`v${selectedConfig.version}`}</Descriptions.Item>
              <Descriptions.Item label={sp('company')}>
                {selectedConfig.companyId?.name || 'Global'}
              </Descriptions.Item>
              <Descriptions.Item label={sp('description')} span={2}>
                {selectedConfig.description || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">{sp('structure')}</Divider>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={sp('headerRow')}>
                {selectedConfig.excelConfig?.headerDetection?.fixedRow ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Data Start Offset">
                {selectedConfig.excelConfig?.dataRows?.startOffset ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Data Row Count">
                {selectedConfig.excelConfig?.dataRows?.fixedCount || 'Auto-detect'}
              </Descriptions.Item>
              <Descriptions.Item label="Skip Sheets">
                {selectedConfig.excelConfig?.skipSheets?.join(', ') || 'None'}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">{sp('columnMappings')}</Divider>
            <div style={{ padding: '8px 0' }}>{renderColumnMappings(selectedConfig.columnMapping)}</div>

            {selectedConfig.testResults && (
              <>
                <Divider orientation="left">{sp('testResults')}</Divider>
                <Descriptions bordered column={3} size="small">
                  <Descriptions.Item label={sp('accuracy')}>
                    <Text type={selectedConfig.testResults.accuracy >= 90 ? 'success' : 'warning'}>
                      {selectedConfig.testResults.accuracy?.toFixed(1)}%
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={sp('sampleSize')}>
                    {selectedConfig.testResults.sampleSize || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={sp('lastTested')}>
                    {selectedConfig.testResults.lastTestedAt
                      ? dayjs(selectedConfig.testResults.lastTestedAt).format('DD.MM.YYYY HH:mm')
                      : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Clone Modal */}
      <Modal
        title={sp('cloneConfiguration')}
        open={cloneModalVisible}
        onCancel={handleCloseCloneModal}
        footer={null}
        width={400}
      >
        <Form form={cloneForm} layout="vertical" onFinish={handleSubmitClone}>
          <Form.Item
            name="name"
            label={sp('newConfigName')}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <Input />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseCloneModal}>{sp('cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={cloneMutation.isPending}>
                {sp('clone')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Format Mapping Modal */}
      <Modal
        title={editingFormatIndex !== null ? 'Edit Format Mapping' : 'Add Format Mapping'}
        open={formatModalVisible}
        onCancel={() => {
          setFormatModalVisible(false);
          formatForm.resetFields();
          setEditingFormatIndex(null);
        }}
        width={700}
        footer={null}
      >
        <Form
          form={formatForm}
          layout="vertical"
          onFinish={handleSaveFormatMapping}
          preserve={true}
        >
          <Tabs
            destroyInactiveTabPane={false}
            items={[
              {
                key: 'basic',
                label: 'Basic',
                children: (
                  <>
                    <Form.Item
                      name="name"
                      label="Format Name"
                      rules={[{ required: true, message: 'Format name is required' }]}
                    >
                      <Input placeholder="e.g., BBP Timesheet, Metro L1P1" />
                    </Form.Item>

                    <Form.Item
                      name="patterns"
                      label={
                        <Space>
                          <span>Filename Patterns</span>
                          <Tooltip title="Comma-separated patterns. Files matching these patterns will use this format.">
                            <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Input placeholder="timesheet, employees (comma separated)" />
                    </Form.Item>

                    <Form.Item name="isDefault" valuePropName="checked">
                      <Checkbox>
                        <Space>
                          <span>Default Format</span>
                          <Tooltip title="Used when no other patterns match">
                            <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </Space>
                      </Checkbox>
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'structure',
                label: 'Structure',
                children: (
                  <>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="headerRow" label="Header Row" initialValue={5}>
                          <InputNumber min={1} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="dataStartOffset" label="Data Start Offset" initialValue={1}>
                          <InputNumber min={0} max={50} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="dataRowCount" label="Data Row Count" initialValue={31}>
                          <InputNumber min={1} max={366} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item name="skipSheets" label="Skip Sheets (comma separated)">
                      <Input placeholder="source, employees, outputs, bank, cash" />
                    </Form.Item>

                    <Form.Item name="employeeNameSource" label="Employee Name Source" initialValue="sheet_name">
                      <Select>
                        <Option value="sheet_name">Sheet Name</Option>
                        <Option value="fixed_cell">Fixed Cell</Option>
                        <Option value="filename_parse">Filename Parse</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item name="jmbgEnabled" valuePropName="checked" initialValue={true}>
                      <Checkbox>Enable JMBG Extraction</Checkbox>
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'columns',
                label: 'Columns',
                children: (
                  <Row gutter={[16, 8]}>
                    {COLUMN_MAPPING_FIELDS.map(({ key, label }) => (
                      <Col span={12} key={key}>
                        <Form.Item name={`col_${key}`} label={label} style={{ marginBottom: 8 }}>
                          <Select allowClear placeholder="Select column">
                            {COLUMN_OPTIONS.map((col) => (
                              <Option key={col} value={col}>{col}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                ),
              },
              {
                key: 'textMarkers',
                label: 'Text Markers',
                children: (
                  <>
                    <Form.Item name="textMarkersEnabled" valuePropName="checked">
                      <Checkbox>
                        <Space>
                          <span>Enable Text Marker Detection</span>
                          <Tooltip title="When cells contain text like 'sick leave' instead of numbers, convert them to hours">
                            <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </Space>
                      </Checkbox>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.textMarkersEnabled !== curr.textMarkersEnabled}>
                      {({ getFieldValue }) =>
                        getFieldValue('textMarkersEnabled') && (
                          <>
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item name="textMarkersSourceColumn" label="Source Column" initialValue="regularHours">
                                  <Select>
                                    {COLUMN_MAPPING_FIELDS.map(({ key, label }) => (
                                      <Option key={key} value={key}>{label}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="textMarkersHoursPerDay" label="Hours Per Day" initialValue={8}>
                                  <InputNumber min={1} max={24} style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Divider orientation="left" plain>Marker Keywords (comma separated)</Divider>

                            <Row gutter={[16, 8]}>
                              <Col span={12}>
                                <Form.Item name="textMarkersAnnualLeave" label="Annual Leave" style={{ marginBottom: 8 }}>
                                  <Input placeholder="annual leave, go, godisnji" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="textMarkersSickLeave" label="Sick Leave" style={{ marginBottom: 8 }}>
                                  <Input placeholder="sick leave, bolovanje" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="textMarkersPaidLeave" label="Paid Leave" style={{ marginBottom: 8 }}>
                                  <Input placeholder="paid leave, special leave" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="textMarkersUnpaidLeave" label="Unpaid Leave" style={{ marginBottom: 8 }}>
                                  <Input placeholder="unpaid leave, neplaceno" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="textMarkersPublicHoliday" label="Public Holiday" style={{ marginBottom: 8 }}>
                                  <Input placeholder="public holiday, praznik" />
                                </Form.Item>
                              </Col>
                            </Row>
                          </>
                        )
                      }
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'summarySheet',
                label: 'Summary Sheet',
                children: (
                  <>
                    <Form.Item name="summarySheetEnabled" valuePropName="checked">
                      <Checkbox>
                        <Space>
                          <span>Enable Summary Sheet Mode (Row per Employee)</span>
                          <Tooltip title="Use when one sheet contains all employees as rows (instead of one sheet per employee)">
                            <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </Space>
                      </Checkbox>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.summarySheetEnabled !== curr.summarySheetEnabled}>
                      {({ getFieldValue }) =>
                        getFieldValue('summarySheetEnabled') && (
                          <>
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item name="summarySheetPattern" label="Sheet Name Pattern">
                                  <Input placeholder="employees, summary (leave empty for first sheet)" />
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item name="summarySheetHeaderRow" label="Header Row" initialValue={1}>
                                  <InputNumber min={1} max={100} style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item name="summarySheetDataStartRow" label="Data Start Row" initialValue={2}>
                                  <InputNumber min={1} max={100} style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Form.Item name="summarySheetSkipIndividual" valuePropName="checked" initialValue={true}>
                              <Checkbox>Skip individual sheets (only parse summary)</Checkbox>
                            </Form.Item>

                            <Form.Item
                              name="summarySheetSkipRowKeywords"
                              label={
                                <Space>
                                  <span>Skip Row Keywords</span>
                                  <Tooltip title="Rows where name contains these keywords will be skipped (e.g., TOTAL, APPROVED BY). Leave empty for defaults.">
                                    <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                                  </Tooltip>
                                </Space>
                              }
                            >
                              <Input placeholder="total, approved, checked (comma separated, leave empty for defaults)" />
                            </Form.Item>

                            <Divider orientation="left" plain>Column Mapping (Row per Employee)</Divider>

                            <Row gutter={[16, 8]}>
                              <Col span={8}>
                                <Form.Item name="summaryCol_employeeName" label="Employee Name" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_jmbg" label="JMBG" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_regularHours" label="Regular Hours" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_overtime" label="Overtime" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_weekendOvertime" label="Weekend OT" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_publicHolidayHours" label="Public Holiday" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_annualLeave" label="Annual Leave" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_sickLeave" label="Sick Leave" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_paidLeave" label="Paid Leave" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_unpaidLeave" label="Unpaid Leave" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_totalHours" label="Total Hours" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                            </Row>

                            <Divider orientation="left" plain>Allowance Columns (for salary configs)</Divider>

                            <Row gutter={[16, 8]}>
                              <Col span={8}>
                                <Form.Item name="summaryCol_bonus" label="Bonus" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_mealAllowance" label="Meal Allowance" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_holidayAllowance" label="Holiday Allowance" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="summaryCol_travelAllowance" label="Travel Allowance" style={{ marginBottom: 8 }}>
                                  <Select allowClear placeholder="Column">
                                    {COLUMN_OPTIONS.map((col) => (
                                      <Option key={col} value={col}>{col}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                            </Row>
                          </>
                        )
                      }
                    </Form.Item>
                  </>
                ),
              },
            ]}
          />

          <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => {
                setFormatModalVisible(false);
                formatForm.resetFields();
                setEditingFormatIndex(null);
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Save Format
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Import Format Mappings Modal */}
      <Modal
        title="Import Format Mappings"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportSourceConfig(null);
          setSelectedImportFormats([]);
        }}
        footer={
          <Space>
            <Button onClick={() => {
              setImportModalVisible(false);
              setImportSourceConfig(null);
              setSelectedImportFormats([]);
            }}>
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={selectedImportFormats.length === 0}
              onClick={() => {
                // Copy selected formats to current config
                const sourceConfig = configs.find((c) => c._id === importSourceConfig);
                if (sourceConfig && sourceConfig.formatMappings) {
                  const formatsToImport = sourceConfig.formatMappings
                    .filter((fm) => selectedImportFormats.includes(fm.id))
                    .map((fm) => ({
                      ...fm,
                      id: `format_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                      isDefault: false, // Never import as default
                    }));

                  setFormatMappings([...formatMappings, ...formatsToImport]);
                  message.success(`Imported ${formatsToImport.length} format mapping(s)`);
                }
                setImportModalVisible(false);
                setImportSourceConfig(null);
                setSelectedImportFormats([]);
              }}
            >
              Import Selected ({selectedImportFormats.length})
            </Button>
          </Space>
        }
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Select source configuration:</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Choose a configuration to import from"
            value={importSourceConfig}
            onChange={(value) => {
              setImportSourceConfig(value);
              setSelectedImportFormats([]);
            }}
          >
            {configs
              .filter((c) => c._id !== selectedConfig?._id && c.formatMappings?.length > 0)
              .map((c) => (
                <Option key={c._id} value={c._id}>
                  {c.name} ({c.formatMappings?.length || 0} formats)
                </Option>
              ))}
          </Select>
        </div>

        {importSourceConfig && (
          <div>
            <Text strong>Select format mappings to import:</Text>
            <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
              <Checkbox.Group
                value={selectedImportFormats}
                onChange={setSelectedImportFormats}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {configs
                    .find((c) => c._id === importSourceConfig)
                    ?.formatMappings?.map((fm) => (
                      <Checkbox key={fm.id} value={fm.id} style={{ width: '100%' }}>
                        <div>
                          <Text strong>{fm.name}</Text>
                          {fm.isDefault && <Tag color="green" style={{ marginLeft: 8 }}>Default</Tag>}
                          {fm.detection?.patterns?.length > 0 && (
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Patterns: {fm.detection.patterns.join(', ')}
                              </Text>
                            </div>
                          )}
                          {fm.summarySheetConfig?.enabled && (
                            <Tag color="cyan" style={{ marginTop: 4 }}>Summary Sheet</Tag>
                          )}
                        </div>
                      </Checkbox>
                    ))}
                </Space>
              </Checkbox.Group>
            </div>
          </div>
        )}

        {!importSourceConfig && (
          <Text type="secondary">
            Select a configuration above to see available format mappings.
          </Text>
        )}
      </Modal>
    </div>
  );
}
